import { Link } from 'react-router-dom';
import { Award, Users, ClipboardList, CheckCircle2, Shield, Headphones, ArrowRight, Wrench, CreditCard, Smartphone, MessageSquare } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { differentials } from '../mock';

const Differentials = () => {
  const iconMap = {
    Award: Award,
    Users: Users,
    ClipboardList: ClipboardList,
    CheckCircle2: CheckCircle2,
    Shield: Shield,
    Headphones: Headphones,
    Wrench: Wrench,
    CreditCard: CreditCard,
    Smartphone: Smartphone,
    MessageSquare: MessageSquare
  };

  return (
    <div className="mt-20">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-gray-900 text-white py-24">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge className="bg-white/20 text-white hover:bg-white/30">
              Nossos Diferenciais
            </Badge>
            <h1 className="text-5xl font-bold">
              Por Que Escolher a LUVCORP
            </h1>
            <p className="text-xl text-blue-100">
              Mais do que uma empresa de reformas, somos parceiros comprometidos com o sucesso do seu projeto e a valorização do seu patrimônio.
            </p>
          </div>
        </div>
      </section>

      {/* Main Differentials */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {differentials.map((diff) => {
              const Icon = iconMap[diff.icon];
              return (
                <Card key={diff.id} className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-blue-300">
                  <CardContent className="p-8 space-y-4">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#15448A] to-[#0d2f5e] flex items-center justify-center text-white group-hover:scale-110 transition-transform">
                      <Icon size={32} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 group-hover:text-[#15448A] transition-colors">
                      {diff.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {diff.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detailed Differentials */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="bg-[#15448A]/10 text-[#15448A] hover:bg-[#15448A]/20 mb-4">
              O Que Nos Torna Únicos
            </Badge>
            <h2 className="text-4xl font-bold mb-4">Compromisso com Excelência em Cada Detalhe</h2>
            <p className="text-lg text-gray-600">
              Nossa reputação foi construída com base em valores sólidos e compromisso inegociável com a qualidade.
            </p>
          </div>

          <div className="max-w-5xl mx-auto space-y-8">
            {/* Detailed Item 1 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-[#15448A] to-[#0d2f5e] flex items-center justify-center text-white">
                  <Shield size={28} />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Segurança em Primeiro Lugar</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    Cumprimos rigorosamente as normas NR-18 e NR-35, garantindo segurança total para trabalhadores, moradores e transeuntes.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-2 text-gray-600">
                      <span className="text-[#15448A] mt-0.5">✓</span>
                      <span>PCMAT elaborado por profissional de segurança</span>
                    </li>
                    <li className="flex items-start space-x-2 text-gray-600">
                      <span className="text-[#15448A] mt-0.5">✓</span>
                      <span>Treinamento NR-35 para todos os trabalhadores em altura</span>
                    </li>
                    <li className="flex items-start space-x-2 text-gray-600">
                      <span className="text-[#15448A] mt-0.5">✓</span>
                      <span>EPIs e EPCs de última geração e certificados</span>
                    </li>
                    <li className="flex items-start space-x-2 text-gray-600">
                      <span className="text-[#15448A] mt-0.5">✓</span>
                      <span>Inspeção diária de equipamentos e andaimes</span>
                    </li>
                    <li className="flex items-start space-x-2 text-gray-600">
                      <span className="text-[#15448A] mt-0.5">✓</span>
                      <span>Seguro de responsabilidade civil vigente</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Detailed Item 2 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center text-white">
                  <ClipboardList size={28} />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Planejamento Meticuloso</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    Cada projeto começa com um planejamento detalhado que considera todos os aspectos técnicos, logísticos e orçamentários.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-2 text-gray-600">
                      <span className="text-[#15448A] mt-0.5">✓</span>
                      <span>Vistoria técnica com laudo fotográfico completo</span>
                    </li>
                    <li className="flex items-start space-x-2 text-gray-600">
                      <span className="text-[#15448A] mt-0.5">✓</span>
                      <span>Cronograma físico-financeiro realista</span>
                    </li>
                    <li className="flex items-start space-x-2 text-gray-600">
                      <span className="text-[#15448A] mt-0.5">✓</span>
                      <span>Memorial descritivo detalhado</span>
                    </li>
                    <li className="flex items-start space-x-2 text-gray-600">
                      <span className="text-[#15448A] mt-0.5">✓</span>
                      <span>Plano de comunicação com moradores</span>
                    </li>
                    <li className="flex items-start space-x-2 text-gray-600">
                      <span className="text-[#15448A] mt-0.5">✓</span>
                      <span>Reuniões periódicas de acompanhamento</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Detailed Item 3 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center text-white">
                  <CheckCircle2 size={28} />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Materiais de Primeira Linha</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    Trabalhamos exclusivamente com marcas reconhecidas e materiais que oferecem durabilidade e garantia de fabricante.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-2 text-gray-600">
                      <span className="text-[#15448A] mt-0.5">✓</span>
                      <span>Tintas Suvinil, Coral e Sherwin-Williams</span>
                    </li>
                    <li className="flex items-start space-x-2 text-gray-600">
                      <span className="text-[#15448A] mt-0.5">✓</span>
                      <span>Massas e argamassas Quartzolit e Vedacit</span>
                    </li>
                    <li className="flex items-start space-x-2 text-gray-600">
                      <span className="text-[#15448A] mt-0.5">✓</span>
                      <span>Impermeabilizantes Sika, Viapol e Weber</span>
                    </li>
                    <li className="flex items-start space-x-2 text-gray-600">
                      <span className="text-[#15448A] mt-0.5">✓</span>
                      <span>Rastreabilidade de todos os materiais aplicados</span>
                    </li>
                    <li className="flex items-start space-x-2 text-gray-600">
                      <span className="text-[#15448A] mt-0.5">✓</span>
                      <span>Garantia estendida de fabricante</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="bg-orange-100 text-[#e66a10] hover:bg-orange-200 mb-4">
              Certificações e Reconhecimentos
            </Badge>
            <h2 className="text-4xl font-bold mb-4">Credibilidade Comprovada</h2>
            <p className="text-lg text-gray-600">
              Nossas certificações e reconhecimentos atestam nosso compromisso com excelência e conformidade.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <div className="text-center p-6 bg-gray-50 rounded-xl hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">🏆</div>
              <h3 className="font-bold text-gray-900 mb-2">ISO 9001</h3>
              <p className="text-sm text-gray-600">Gestão de Qualidade</p>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-xl hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">✅</div>
              <h3 className="font-bold text-gray-900 mb-2">NR-18/35</h3>
              <p className="text-sm text-gray-600">Segurança do Trabalho</p>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-xl hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">📜</div>
              <h3 className="font-bold text-gray-900 mb-2">ART Ativa</h3>
              <p className="text-sm text-gray-600">Responsabilidade Técnica</p>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-xl hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">🛡️</div>
              <h3 className="font-bold text-gray-900 mb-2">Seguro RC</h3>
              <p className="text-sm text-gray-600">Responsabilidade Civil</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-4xl font-bold">
              Experimente a Diferença LUVCORP
            </h2>
            <p className="text-xl text-blue-100">
              Entre em contato e descubra como nossos diferenciais podem transformar seu projeto.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contato">
                <Button size="lg" className="bg-white text-[#15448A] hover:bg-gray-100 shadow-xl">
                  Solicitar Orçamento
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/projetos">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10">
                  Ver Projetos
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Differentials;